import { defineMessages, useIntl } from "react-intl";

import {
  queryClient,
  MutationActions,
  QueryKeys,
  useEntityMutation,
} from "@/api/core";
import { useApi } from "@/hooks";
import { type Status, statusSchema } from "@/schemas";
import toast from "@/toast";

import { updateStatusCache } from "../utils";

const messages = defineMessages({
  unpinSuccess: {
    id: "status.unpin.success",
    defaultMessage: "Unpinned from Profile!",
  },
  deletedTruth: {
    id: "status.error.deletedTruth",
    defaultMessage: "This Truth has been deleted and is no longer available.",
  },
});

interface Variables {
  id: string;
}

function useUnpinStatus() {
  const api = useApi();
  const intl = useIntl();

  return useEntityMutation({
    globalQueryKey: QueryKeys.statuses.global,
    idAttributeFromVariables: "id",
    action: MutationActions.UPDATE,
    schema: statusSchema,
    mutationFn: (variables: Variables) =>
      api.post<Status>(`/api/v1/statuses/${variables.id}/unpin`),
    onMutate(variables) {
      const [prevStatus] = updateStatusCache(variables.id, () => ({
        pinned: false,
      }));

      return { prevStatus };
    },
    onSuccess(response) {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.statuses.account(response.data.account.id, {
          pinned: true,
          with_muted: true,
        }),
      });

      toast.success(intl.formatMessage(messages.unpinSuccess));
    },
    onError(error, variables, context) {
      if (error.response?.status === 404) {
        toast.error(intl.formatMessage(messages.deletedTruth));
      }
      if (context) {
        queryClient.setQueryData(
          QueryKeys.statuses.detail(variables.id),
          context.prevStatus,
        );
      }
    },
  });
}

export { useUnpinStatus };
